import { Button } from "../components/ui/button";
import Dropdown from "../../utils/Dropdown";
import { RootState } from "../../store/store";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearAnalysis, setPlaybookMetadata } from "../../store/playbook-analysis-slice";
import { contractTypes } from "../../utils/contract";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FileText, Loader2, UploadCloud, BookText, Trash2 } from "lucide-react";
import { BackButton } from "./back-button";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { ClauseCell } from "./clause-cell";
import { IPlaybookClause } from "../hooks/playbook-clause";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "../lib/utils";

export function UploadPlaybook() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const qc = useQueryClient();

	const [loading, setLoading] = useState<boolean>(false);
	const [showContractTypes, setshowContractTypes] = useState(false);
	const [isClauseSegmented, setIsClauseSegmented] = useState<boolean>(true);

	const [file, setFile] = useState<File>();
	// ref for the input element
	// reset the value of the input ref when removing the file
	// so that it resets the value of input element
	const inputRef = useRef<HTMLInputElement>(null);

	const [showClauses, setShowClauses] = useState<boolean>(false);

	const [clauses, setClauses] = useState<IPlaybookClause[]>([]);
	const [selectedContractType, setSelectedContractType] = useState<any>({
		label: "",
		value: "",
	});

	const playbookId = useSelector((state: RootState) => state.analysis.playbookMetadata?.id);

	const fileData = useSelector((state: RootState) => state.files);

	useEffect(() => {
		setSelectedContractType(fileData.fileType);
	}, []);

	const processPlaybook = async () => {
		try {
			setshowContractTypes(false);
			setLoading(true);

			if (!file) {
				throw new Error("No file type selected");
			}

			const formData = new FormData();
			if (file.type === "text/csv") {
				formData.append("playbook_type", selectedContractType.value);
			}
			formData.append("file", file);
			formData.append("segment_clause", isClauseSegmented ? "true" : "false");

			const res: any = await api.post("/api/v1/playbook/generate-playbook", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			if (!res.success) {
				throw new Error(res.message ?? "failed to upload the playbook");
			}

			dispatch(setPlaybookMetadata({ id: res.playbook_id, name: res.playbook_name }));

			const clauses: any = await api.get("/api/v1/playbook/clauses", {
				params: {
					playbook_id: res.playbook_id,
				},
			});

			if (!clauses.success) {
				throw new Error("Failed to fetch playbook clauses");
			}

			setClauses(clauses.data);
			toast.success("Your upload is complete");
		} catch (error) {
			toast.error(String(error));
		} finally {
			setshowContractTypes(false);
			setLoading(false);
		}
	};

	const triggerRiskAnalysis = async () => {
		const res: any = await api.post("api/v1/playbook/activate-playbook", {
			contractType: fileData.fileType.value,
			playbookId,
		});

		if (!res.success) {
			toast.error("failed to activate the playbook");
			return;
		}

		dispatch(clearAnalysis());
		qc.setQueryData(["analyze-playbook"], null);
		navigate("/analyze");
	};

	const handleContractTypeChange = (value: object) => {
		if (value) {
			setSelectedContractType(value);
		}
	};

	return (
		<div className="p-4 flex flex-col gap-3 max-h-screen">
			<div className="self-stretch">
				<BackButton />
			</div>

			<article className="w-full p-3 border rounded-lg">
				<Label
					htmlFor="file-upload"
					className={cn(
						"flex items-center justify-center w-full h-11 border-2 border-dashed border-gray-300 rounded-md hover:border-gray-500 transition group",
						!file && "cursor-pointer",
						file && "border-gray-500"
					)}
				>
					<UploadCloud
						className={cn("size-7 text-gray-400 group-hover:text-gray-500 transition", file && "text-gray-500")}
					/>
					<input
						id="file-upload"
						type="file"
						accept=".csv, .docx, .pdf"
						className="hidden"
						required
						ref={inputRef}
						disabled={loading || !!clauses.length || !!file}
						onChange={(e) => {
							const file = e.target.files[0] ?? null;
							setFile(file);
							setClauses([]);
							setshowContractTypes(true);
						}}
					/>
				</Label>

				<p className="mt-1 text-gray-600 text-sm w-full flex gap-2 justify-center items-center font-semibold">
					{file ? (
						<>
							{(!!clauses.length || !loading) && (
								<Trash2
									className="size-4 text-red-400 cursor-pointer"
									onClick={() => {
										setFile(null);
										setShowClauses(false);
										if (inputRef.current) {
											inputRef.current.value = null;
										}
									}}
								/>
							)}
							<span className="truncate">{file.name}</span>
						</>
					) : (
						<>
							<FileText className="size-4 shrink-0" />
							<span className="truncate">Choose File</span>
						</>
					)}
				</p>

				<p className="text-xs text-gray-400 text-center mt-1">
					Please upload your standard contract in DOCX, CSV or PDF format only.
				</p>
			</article>

			{!clauses.length && (
				<div className="border rounded-lg p-3">
					<Link
						to="https://devlegalgraph.blob.core.windows.net/app-files/Sample%20CSV%20Playbook.csv"
						download="Sample CSV Playbook.csv"
						className="text-sm flex items-center gap-2 justify-center text-blue-400"
					>
						<BookText className="size-4" />
						Download sample CSV playbook
					</Link>

					<p className="text-center text-xs text-gray-400 mt-2 w-9/10 mx-auto">
						Download a sample playbook to see how playbook standards are structured and formatted.
					</p>
				</div>
			)}

			{showContractTypes && file?.type === "text/csv" && (
				<div className="mb-4">
					<label htmlFor="contractType" className="block mb-2 text-base font-medium text-[#08081A]">
						Please select your contract type
					</label>
					<Dropdown options={contractTypes} onSelect={handleContractTypeChange} initialValue={fileData.fileType} />
					<p className="text-[10px] font-normal text-left text-[#61607E] pt-1">
						Please select the contract type from the dropdown to proceed with your playbook upload
					</p>
				</div>
			)}

			{!!clauses.length && (
				<div className="rounded-lg w-full border p-3">
					<div className="flex justify-between items-center">
						<p className="text-sm">Show Playbok Clauses</p>
						<Switch checked={showClauses} onCheckedChange={setShowClauses} />
					</div>
				</div>
			)}

			{showClauses && (
				<div className="overflow-auto flex-1 border rounded-lg grid gap-2 p-3 overscroll-none">
					<>
						{clauses.map((clause, idx) => (
							<ClauseCell key={idx} clause={clause} />
						))}
					</>
				</div>
			)}

			<div className="flex justify-center flex-col">
				{clauses.length > 0 ? (
					<Button disabled={clauses.length === 0} onClick={() => triggerRiskAnalysis()}>
						Start Risk Analysis
					</Button>
				) : (
					<Button disabled={!file || loading} onClick={() => processPlaybook()}>
						{loading ? (
							<div className="flex items-center justify-center gap-2">
								<Loader2 className="size-4 animate-spin" />
								Uploading...
							</div>
						) : (
							"Upload"
						)}
					</Button>
				)}
			</div>
		</div>
	);
}
